import { getTimeZone } from "../../../utils";

// ─── Types ────────────────────────────────────────────────────────────────────

export type SubscriptionFrequency = "hourly" | "daily" | "weekly" | "monthly";
export type SubscriptionDestinationType = "email" | "teams" | "slack";
export type SubscriptionScope = "entire" | "specific";

export interface SubscriptionDestination {
  type: SubscriptionDestinationType;
  recipients: string[];
}

export interface DashboardSubscription {
  id: string;
  frequency: SubscriptionFrequency;
  /** Human-readable schedule label, e.g. "Every Friday at 5:00 PM" */
  scheduleLabel: string;
  scope: SubscriptionScope;
  blockIds?: string[];
  includeAiSummary: boolean;
  destinations: SubscriptionDestination[];
  paused?: boolean;
  hourIndex?: number;
  /** IANA zone from API schedule (`tz_name`); used for hour labels when showing/editing saved subs */
  scheduleTzName?: string;
  /** 0–6 (Sun–Sat), weekly schedules only; sorted unique when saved */
  weekdays?: number[];
  daysOfMonth?: string;
  lastRunDt?: string;
  lastRunFailed?: boolean;
  lastRunErrorMessage?: string;
}

export interface SubscriptionFormState {
  frequency: SubscriptionFrequency;
  hourIndex: number;
  /** Selected weekdays 0–6 (Sun–Sat); at least one when frequency is weekly */
  weekdays: number[];
  daysOfMonth: string;
  scope: SubscriptionScope;
  blockIds: string[];
  includeAiSummary: boolean;
  destinations: SubscriptionDestination[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const isOffsetStyleTzName = (name: string) => {
  const t = name.trim();
  // Also match Unicode minus (U+2212) and non-ASCII digit sequences that some
  // locales/browsers emit (e.g. "GMT−७" in hi-IN).
  return /^(GMT|UTC)[\u002B\u002D\u2212\u2010\u2011]/i.test(t) || /^UTC$/i.test(t);
};

/** Build a clean ASCII "GMT±H:MM" label from the actual UTC offset of a timezone. */
const offsetLabel = (timeZoneId: string, refDate: Date): string => {
  try {
    // Get the offset in minutes via Intl with a known-ASCII format
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timeZoneId,
      timeZoneName: "shortOffset",
    }).formatToParts(refDate);
    const raw = parts.find(p => p.type === "timeZoneName")?.value ?? "";
    // shortOffset always gives ASCII e.g. "GMT+5:30", "GMT-7", "GMT"
    if (/^GMT([+-]\d+(:\d+)?)?$/.test(raw)) {
      return raw;
    }
    // Fallback: compute offset manually
    const utcDate = new Date(refDate.toLocaleString("en-US", { timeZone: "UTC" }));
    const tzDate = new Date(refDate.toLocaleString("en-US", { timeZone: timeZoneId }));
    const diffMin = Math.round((tzDate.getTime() - utcDate.getTime()) / 60000);
    if (diffMin === 0) {
      return "GMT";
    }
    const sign = diffMin > 0 ? "+" : "-";
    const abs = Math.abs(diffMin);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    return m === 0 ? `GMT${sign}${h}` : `GMT${sign}${h}:${String(m).padStart(2, "0")}`;
  } catch {
    return "GMT";
  }
};

const formatTimeZoneNamePart = (
  timeZoneId: string,
  refDate: Date,
  timeZoneName: Intl.DateTimeFormatOptions["timeZoneName"],
  locale?: Intl.UnicodeBCP47LocaleIdentifier | Intl.UnicodeBCP47LocaleIdentifier[]
): string | undefined => {
  try {
    const parts = (
      locale === undefined
        ? new Intl.DateTimeFormat(undefined, { timeZone: timeZoneId, timeZoneName })
        : new Intl.DateTimeFormat(locale, { timeZone: timeZoneId, timeZoneName })
    ).formatToParts(refDate);
    return parts.find(p => p.type === "timeZoneName")?.value?.trim();
  } catch {
    return undefined;
  }
};

/** Strip trailing "Standard/Daylight/Summer Time" for a readable place name (e.g. Moscow, Pacific). */
const namedFromLongTimeZone = (longName: string): string | undefined => {
  const stripped = longName.replace(/\s+(Standard|Daylight|Summer)\s+Time$/i, "").trim();
  if (!stripped || isOffsetStyleTzName(stripped)) {
    return undefined;
  }
  return stripped;
};

/** Short TZ label for a given IANA zone (e.g. IST, MSK, EST) for `refDate` (DST-aware where applicable). */
export const getTimeZoneAbbreviation = (timeZoneId: string, refDate: Date = new Date()): string => {
  const key = timeZoneId.trim();
  try {
    // `en-US` often gives `GMT+5:30` for India; `en-IN` typically yields `IST` — still pure Intl, no hardcoded map.
    for (const locale of [undefined, "en-US", "en-IN"] as const) {
      const short = formatTimeZoneNamePart(key, refDate, "short", locale);
      if (short && !isOffsetStyleTzName(short)) {
        return short;
      }
    }

    const shortGeneric = formatTimeZoneNamePart(key, refDate, "shortGeneric", "en-US");
    if (shortGeneric && !isOffsetStyleTzName(shortGeneric)) {
      return shortGeneric;
    }

    const longName = formatTimeZoneNamePart(key, refDate, "long", "en-US");
    const fromLong = longName ? namedFromLongTimeZone(longName) : undefined;
    if (fromLong) {
      return fromLong;
    }

    // No named abbreviation exists — use a clean ASCII GMT offset.
    return offsetLabel(key, refDate);
  } catch {
    return offsetLabel(timeZoneId, refDate);
  }
};

/** Human-readable clock options for the hour dropdown (aligned with subscription `hour` index). */
export const getScheduleHourLabels = (timeZoneId: string = getTimeZone()): string[] => {
  const tzAbbr = getTimeZoneAbbreviation(timeZoneId);
  return Array.from({ length: 24 }, (_, i) => {
    const h = i % 12 || 12;
    const ampm = i < 12 ? "AM" : "PM";
    return `${h}:00 ${ampm} ${tzAbbr}`;
  });
};

export const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export const FREQUENCY_OPTIONS: { value: SubscriptionFrequency; label: string }[] = [
  { value: "hourly", label: "Hourly" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

export const SCOPE_OPTIONS: { value: SubscriptionScope; label: string }[] = [
  { value: "entire", label: "Entire dashboard" },
  { value: "specific", label: "Specific blocks" },
];

export const DESTINATION_TYPE_OPTIONS: { value: SubscriptionDestinationType; label: string }[] = [
  { value: "email", label: "Email" },
  { value: "slack", label: "Slack" },
  { value: "teams", label: "Microsoft Teams" },
];

/** Empty-state "Where to send" chips. */
export const DESTINATION_TYPE_OPTIONS_SUBSCRIBE_MODAL: {
  value: SubscriptionDestinationType;
  label: string;
}[] = DESTINATION_TYPE_OPTIONS;

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const buildScheduleLabel = (
  sub: Omit<DashboardSubscription, "id" | "scheduleLabel">,
  explicitTimeZoneId?: string
): string => {
  const timeZoneId = explicitTimeZoneId ?? sub.scheduleTzName ?? getTimeZone();
  const hourLabels = getScheduleHourLabels(timeZoneId);
  const hourLabel = hourLabels[sub.hourIndex ?? 9];
  if (sub.frequency === "hourly") {
    return "Hourly";
  }
  if (sub.frequency === "daily") {
    return `Daily at ${hourLabel}`;
  }
  if (sub.frequency === "weekly") {
    const raw = sub.weekdays?.length ? sub.weekdays : [5];
    const unique = [...new Set(raw)].sort((a, b) => a - b);
    const shortNames = unique.map(i => WEEKDAYS[i]?.slice(0, 3) ?? "?");
    const dayList =
      shortNames.length > 2
        ? `${shortNames.slice(0, -1).join(", ")} & ${shortNames[shortNames.length - 1]}`
        : shortNames.join(" & ");
    return `Every ${dayList} at ${hourLabel}`;
  }
  if (sub.frequency === "monthly") {
    const parsed = (sub.daysOfMonth || "1")
      .split(",")
      .map(s => parseInt(s.trim(), 10))
      .filter(n => Number.isInteger(n) && n >= 1 && n <= 31);
    const unique = [...new Set(parsed)].sort((a, b) => a - b);
    if (unique.length === 1 && unique[0] === 31) {
      return `Monthly on last day of month at ${hourLabel}`;
    }
    const human = unique.map(d => (d === 31 ? "last day" : String(d))).join(", ");
    return `Monthly on days ${human} at ${hourLabel}`;
  }
  return "";
};

export const defaultFormState = (): SubscriptionFormState => ({
  frequency: "daily",
  hourIndex: 9,
  weekdays: [5],
  daysOfMonth: "1, 15",
  scope: "entire",
  blockIds: [],
  includeAiSummary: true,
  destinations: [],
});

export const subscriptionToFormState = (sub: DashboardSubscription): SubscriptionFormState => ({
  frequency: sub.frequency,
  hourIndex: sub.hourIndex ?? 9,
  weekdays: sub.weekdays?.length ? [...sub.weekdays] : [5],
  daysOfMonth: sub.daysOfMonth ?? "1, 15",
  scope: sub.scope,
  blockIds: sub.blockIds || [],
  includeAiSummary: sub.includeAiSummary,
  destinations: sub.destinations.map(d => ({ ...d, recipients: [...d.recipients] })),
});
